// src/utils/supplierCompliance.ts
// Sprint 3 — G10 / R10
// Supplier compliance expiry tracking: QBCC licence, public liability, workers comp
// Flags suppliers expiring within 60 days; feeds ProcurementOverview KPI card

export type ComplianceDocType =
  | 'QBCC_LICENCE'
  | 'PUBLIC_LIABILITY'
  | 'WORKERS_COMP'
  | 'PROFESSIONAL_INDEMNITY'
  | 'SWMS_CURRENT'

export type ComplianceStatus = 'VALID' | 'EXPIRING_SOON' | 'EXPIRED' | 'NOT_PROVIDED'

export const COMPLIANCE_EXPIRY_THRESHOLD_DAYS = 60

export interface SupplierComplianceRecord {
  supplierId:    string
  supplierName:  string
  docType:       ComplianceDocType
  docReference:  string | null
  expiryDate:    string | null   // ISO date
  status:        ComplianceStatus
  daysUntilExpiry: number | null
  lastChecked:   string          // ISO datetime
}

export const DOC_TYPE_META: Record<ComplianceDocType, { label: string; short: string }> = {
  QBCC_LICENCE:           { label: 'QBCC Licence',              short: 'QBCC' },
  PUBLIC_LIABILITY:       { label: 'Public Liability Insurance', short: 'PL Ins.' },
  WORKERS_COMP:           { label: 'Workers Compensation',       short: 'Workers Comp' },
  PROFESSIONAL_INDEMNITY: { label: 'Professional Indemnity',     short: 'PI Ins.' },
  SWMS_CURRENT:           { label: 'SWMS Current',               short: 'SWMS' },
}

export const COMPLIANCE_STATUS_META: Record<ComplianceStatus, {
  label: string; bg: string; text: string; border: string; dot: string
}> = {
  VALID:          { label: 'Valid',          bg: 'bg-green-50',  text: 'text-green-700',  border: 'border-green-200',  dot: 'bg-green-500' },
  EXPIRING_SOON:  { label: 'Expiring soon',  bg: 'bg-amber-50',  text: 'text-amber-700',  border: 'border-amber-200',  dot: 'bg-amber-400' },
  EXPIRED:        { label: 'Expired',        bg: 'bg-red-50',    text: 'text-red-700',    border: 'border-red-200',    dot: 'bg-red-500' },
  NOT_PROVIDED:   { label: 'Not provided',   bg: 'bg-gray-50',   text: 'text-gray-500',   border: 'border-gray-200',   dot: 'bg-gray-300' },
}

// ── Derive status from expiry date ─────────────────────────────────

export function deriveComplianceStatus(expiryDate: string | null): {
  status: ComplianceStatus; daysUntilExpiry: number | null
} {
  if (!expiryDate) return { status: 'NOT_PROVIDED', daysUntilExpiry: null }
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const expiry = new Date(expiryDate)
  const diffMs  = expiry.getTime() - today.getTime()
  const days    = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (days < 0)  return { status: 'EXPIRED',       daysUntilExpiry: days }
  if (days <= COMPLIANCE_EXPIRY_THRESHOLD_DAYS) return { status: 'EXPIRING_SOON', daysUntilExpiry: days }
  return { status: 'VALID', daysUntilExpiry: days }
}

// ── Mock compliance data ───────────────────────────────────────────

const today = new Date()
const daysFrom = (d: number) => {
  const dt = new Date(today)
  dt.setDate(dt.getDate() + d)
  return dt.toISOString().split('T')[0]
}

export const MOCK_SUPPLIER_COMPLIANCE: SupplierComplianceRecord[] = [
  // sup001 — Prysmian (Olex) — all valid
  { supplierId: 'sup001', supplierName: 'Prysmian Group (Olex)',  docType: 'PUBLIC_LIABILITY',       docReference: 'PLI-2026-0441', expiryDate: daysFrom(280), ...deriveComplianceStatus(daysFrom(280)), lastChecked: today.toISOString() },
  { supplierId: 'sup001', supplierName: 'Prysmian Group (Olex)',  docType: 'WORKERS_COMP',           docReference: 'WC-QLD-2026-88', expiryDate: daysFrom(210), ...deriveComplianceStatus(daysFrom(210)), lastChecked: today.toISOString() },

  // sup002 — Anixter — QBCC expiring in 25 days
  { supplierId: 'sup002', supplierName: 'Anixter Australia',      docType: 'QBCC_LICENCE',           docReference: 'QBCC-SC-14822', expiryDate: daysFrom(25),  ...deriveComplianceStatus(daysFrom(25)),  lastChecked: today.toISOString() },
  { supplierId: 'sup002', supplierName: 'Anixter Australia',      docType: 'PUBLIC_LIABILITY',       docReference: 'PLI-ANX-2026', expiryDate: daysFrom(365), ...deriveComplianceStatus(daysFrom(365)), lastChecked: today.toISOString() },
  { supplierId: 'sup002', supplierName: 'Anixter Australia',      docType: 'WORKERS_COMP',           docReference: 'WC-ANX-2026', expiryDate: daysFrom(300),  ...deriveComplianceStatus(daysFrom(300)), lastChecked: today.toISOString() },

  // sup003 — Schneider — workers comp expired
  { supplierId: 'sup003', supplierName: 'Schneider Electric',     docType: 'PUBLIC_LIABILITY',       docReference: 'PLI-SE-2026', expiryDate: daysFrom(180),  ...deriveComplianceStatus(daysFrom(180)), lastChecked: today.toISOString() },
  { supplierId: 'sup003', supplierName: 'Schneider Electric',     docType: 'WORKERS_COMP',           docReference: null, expiryDate: daysFrom(-10), ...deriveComplianceStatus(daysFrom(-10)), lastChecked: today.toISOString() },

  // sup004 — Condux — all valid, has QBCC
  { supplierId: 'sup004', supplierName: 'Condux International',   docType: 'QBCC_LICENCE',           docReference: 'QBCC-EL-29901', expiryDate: daysFrom(320), ...deriveComplianceStatus(daysFrom(320)), lastChecked: today.toISOString() },
  { supplierId: 'sup004', supplierName: 'Condux International',   docType: 'PUBLIC_LIABILITY',       docReference: 'PLI-CX-2026', expiryDate: daysFrom(290),  ...deriveComplianceStatus(daysFrom(290)), lastChecked: today.toISOString() },
  { supplierId: 'sup004', supplierName: 'Condux International',   docType: 'WORKERS_COMP',           docReference: 'WC-CX-2026', expiryDate: daysFrom(310),   ...deriveComplianceStatus(daysFrom(310)), lastChecked: today.toISOString() },

  // sup005 — Traffic Signal Solutions — expiring in 45 days
  { supplierId: 'sup005', supplierName: 'Traffic Signal Solutions', docType: 'QBCC_LICENCE',         docReference: 'QBCC-TC-55512', expiryDate: daysFrom(45),  ...deriveComplianceStatus(daysFrom(45)),  lastChecked: today.toISOString() },
  { supplierId: 'sup005', supplierName: 'Traffic Signal Solutions', docType: 'PUBLIC_LIABILITY',     docReference: 'PLI-TSS-2026', expiryDate: daysFrom(200), ...deriveComplianceStatus(daysFrom(200)), lastChecked: today.toISOString() },
  { supplierId: 'sup005', supplierName: 'Traffic Signal Solutions', docType: 'PROFESSIONAL_INDEMNITY', docReference: null, expiryDate: null, ...deriveComplianceStatus(null), lastChecked: today.toISOString() },
]

// ── Helper functions ───────────────────────────────────────────────

export function getComplianceForSupplier(supplierId: string): SupplierComplianceRecord[] {
  return MOCK_SUPPLIER_COMPLIANCE.filter(r => r.supplierId === supplierId)
}

export function getSupplierComplianceStatus(supplierId: string): ComplianceStatus {
  const records = getComplianceForSupplier(supplierId)
  if (records.length === 0) return 'NOT_PROVIDED'
  if (records.some(r => r.status === 'EXPIRED'))       return 'EXPIRED'
  if (records.some(r => r.status === 'EXPIRING_SOON')) return 'EXPIRING_SOON'
  if (records.some(r => r.status === 'NOT_PROVIDED'))  return 'NOT_PROVIDED'
  return 'VALID'
}

/** Returns count of suppliers with compliance issues (expired or expiring soon) */
export function getComplianceIssueCount(): number {
  const supplierIds = new Set(MOCK_SUPPLIER_COMPLIANCE.map(r => r.supplierId))
  let count = 0
  for (const id of supplierIds) {
    const s = getSupplierComplianceStatus(id)
    if (s === 'EXPIRED' || s === 'EXPIRING_SOON') count++
  }
  return count
}

export function getSuppliersWithIssues(): string[] {
  const supplierIds = new Set(MOCK_SUPPLIER_COMPLIANCE.map(r => r.supplierId))
  return Array.from(supplierIds).filter(id => {
    const s = getSupplierComplianceStatus(id)
    return s === 'EXPIRED' || s === 'EXPIRING_SOON'
  })
}
